import { Link } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import SectionHeading from "@/components/base/SectionHeading";
import Reveal from "@/components/feature/Reveal";

const history = [
  { year: "1994", title: "일신석재 설립", desc: "서울 성동구에 대리석 가공 공장 설립" },
  { year: "2003", title: "직수입 체계 구축", desc: "이탈리아·터키 원석 직수입 라인 확보" },
  { year: "2011", title: "서울 본사 이전", desc: "강동구 성내로 본사 및 전시장 확장 이전" },
  { year: "2018", title: "엔지니어드 스톤 도입", desc: "쿼츠·테라조 라인 신설, 상판 전문 시공팀 구성" },
  { year: "2023", title: "부산 전시장 준비", desc: "영남권 고객을 위한 대형 전시 공간 조성" },
  { year: "2026", title: "부산 전시장 그랜드 오픈", desc: "해운대 달맞이길에 5층 규모 전시장 개관" },
];

const businessAreas = [
  {
    icon: "ri-home-4-line",
    title: "주거 공간",
    desc: "주방 상판, 욕실, 거실 바닥, 현관 등 집 안 곳곳의 석재 시공을 담당합니다.",
  },
  {
    icon: "ri-store-2-line",
    title: "상업 공간",
    desc: "카페, 레스토랑, 살롱 등 브랜드 이미지를 담는 상업 공간의 마감을 설계합니다.",
  },
  {
    icon: "ri-hotel-line",
    title: "호텔 · 리조트",
    desc: "로비와 객실, 스파 등 대형 프로젝트를 안정적인 물량과 일정으로 진행합니다.",
  },
  {
    icon: "ri-building-4-line",
    title: "건축 · 외장",
    desc: "외벽, 계단, 조경석 등 내구성이 필요한 구조 요소에 적합한 석재를 공급합니다.",
  },
];

const locations = [
  {
    name: "서울 본사",
    address: "서울특별시 강동구 성내로 19, 6층",
    phone: "02-487-9009",
    fax: "02-477-5278",
    hours: "평일 09:00 - 18:00",
    mapSrc:
      "https://www.google.com/maps?q=%EC%84%9C%EC%9A%B8%ED%8A%B9%EB%B3%84%EC%8B%9C%20%EA%B0%95%EB%8F%99%EA%B5%AC%20%EC%84%B1%EB%82%B4%EB%A1%9C%2019&output=embed",
  },
  {
    name: "부산 전시장",
    address: "부산광역시 해운대구 달맞이길 120-30, 5층",
    phone: "051-741-2045",
    fax: "051-741-2046",
    hours: "평일 09:30 - 18:00 / 토 10:00 - 15:00",
    mapSrc:
      "https://www.google.com/maps?q=%EB%B6%80%EC%82%B0%EA%B4%91%EC%97%AD%EC%8B%9C%20%ED%95%B4%EC%9A%B4%EB%8C%80%EA%B5%AC%20%EB%8B%AC%EB%A7%9E%EC%9D%B4%EA%B8%B8%20120-30&output=embed",
  },
];

export default function About() {
  return (
    <>
      <PageBanner
        eyebrow="ABOUT US"
        title="회사소개"
        description="1994년부터 석재 하나만 다뤄온 동우스톤. 사람과 공간, 그리고 소재에 대한 이야기를 전합니다."
        image="https://readdy.ai/api/search-image?query=modern%20company%20showroom%20interior%20with%20stone%20samples%20and%20display%20areas%2C%20warm%20neutral%20lighting%2C%20minimal%20professional%20space%2C%20clean%20architectural%20photography%2C%20calm%20atmosphere&width=1800&height=900&seq=stone-banner-about&orientation=landscape"
        imageAlt="동우스톤 본사 전시 공간"
        current="회사소개"
      />

      {/* Greeting */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="h-[360px] w-full overflow-hidden rounded-lg md:h-[480px]">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20welcoming%20stone%20showroom%20interior%20with%20marble%20samples%20arranged%20on%20display%20stands%2C%20soft%20natural%20daylight%2C%20neutral%20beige%20and%20grey%20palette%2C%20refined%20interior%20photography%2C%20calm%20atmosphere&width=1100&height=1300&seq=stone-about-greeting&orientation=portrait"
                  alt="소재 샘플이 정리된 동우스톤 전시 공간"
                  title="동우스톤 소재 전시"
                  className="h-full w-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <SectionHeading
                eyebrow="GREETING"
                title={
                  <>
                    돌은 거짓말을
                    <br />
                    하지 않습니다
                  </>
                }
                description="석재는 한 번 시공하면 쉽게 바꾸기 어려운 마감입니다. 그래서 동우스톤은 소재를 권하기 전에 공간의 쓰임과 생활 방식을 먼저 묻습니다."
              />
              <div className="mt-6 space-y-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                <p>
                  같은 대리석이라도 밝은 공간과 어두운 공간에서 전혀 다르게 보입니다. 아이가 뛰노는 집과 조용한
                  작업실에 어울리는 소재도 다릅니다. 저희가 전시장에서 실물을 강조하는 이유입니다.
                </p>
                <p>
                  화려한 마케팅보다 정확한 상담과 꼼꼼한 시공으로 신뢰를 쌓아왔습니다. 앞으로도 동우스톤은
                  변하지 않는 기준으로 공간을 완성하겠습니다.
                </p>
              </div>
              <div className="mt-8 flex items-center gap-4 border-t border-background-200 pt-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-user-star-line text-xl" />
                </span>
                <div>
                  <p className="text-sm text-foreground-600">동우스톤 대표</p>
                  <p className="font-heading text-lg text-foreground-950">홍길동</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* History */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <Reveal>
            <SectionHeading eyebrow="HISTORY" title="걸어온 길" description="동우스톤이 지나온 시간을 연도별로 정리했습니다." />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {history.map((item, index) => (
              <Reveal key={item.year} delay={index * 70}>
                <div className="h-full rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="font-heading text-2xl text-accent-600">{item.year}</span>
                  <h3 className="mt-3 font-heading text-lg text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Business areas */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <Reveal>
            <SectionHeading
              align="center"
              eyebrow="BUSINESS"
              title="다루는 공간"
              description="주거부터 상업, 호텔, 외장까지. 용도에 맞는 소재와 시공 방식을 제안합니다."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {businessAreas.map((area, index) => (
              <Reveal key={area.title} delay={index * 70}>
                <div className="h-full rounded-lg border border-background-200 bg-background-100 p-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${area.icon} text-2xl`} />
                  </span>
                  <h3 className="mt-5 font-heading text-lg text-foreground-950">{area.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{area.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="LOCATION"
              title="전시장 안내"
              description="서울과 부산 두 곳의 전시장에서 실물 소재를 직접 확인하실 수 있습니다."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {locations.map((location, index) => (
              <Reveal key={location.name} delay={index * 90}>
                <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  <iframe
                    title={`${location.name} 위치 지도`}
                    src={location.mapSrc}
                    aria-label={`${location.name} 위치`}
                    className="h-[260px] w-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  <div className="p-6">
                    <h3 className="font-heading text-xl text-foreground-950">{location.name}</h3>
                    <ul className="mt-4 space-y-3 text-sm text-foreground-600">
                      <li className="flex gap-3">
                        <i className="ri-map-pin-line mt-0.5 shrink-0 text-base text-primary-600" />
                        {location.address}
                      </li>
                      <li className="flex gap-3">
                        <i className="ri-phone-line mt-0.5 shrink-0 text-base text-primary-600" />
                        T. {location.phone}
                      </li>
                      <li className="flex gap-3">
                        <i className="ri-printer-line mt-0.5 shrink-0 text-base text-primary-600" />
                        F. {location.fax}
                      </li>
                      <li className="flex gap-3">
                        <i className="ri-time-line mt-0.5 shrink-0 text-base text-primary-600" />
                        {location.hours}
                      </li>
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary-500">
        <div className="mx-auto flex max-w-content flex-col items-center gap-6 px-4 py-14 text-center md:px-6 md:py-16">
          <h2 className="max-w-2xl font-heading text-2xl leading-snug text-background-50 md:text-3xl">
            동우스톤과 함께 공간을 완성해 보세요
          </h2>
          <p className="max-w-xl text-sm leading-relaxed text-background-100/90">
            소재 선정부터 시공, 사후 관리까지 한 팀이 책임집니다. 궁금한 점은 언제든 문의해 주세요.
          </p>
          <Link
            to="/contact"
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-7 py-3.5 text-sm font-medium text-primary-700 transition-colors hover:bg-background-100"
          >
            고객문의 바로가기 <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </>
  );
}